import {
  Dispatch,
  Fragment,
  memo,
  ReactElement,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Divider } from '@breathelife/mui';

import { ProductsEntity, SignatureType } from '@breathelife/types';
import { ApplicationMode, ApplicationModes, getSectionById, RenderingQuestionnaire } from '@zinnia/form-engine-sdk';

import {
  Direction,
  findSectionGroup,
  getSectionGroupWithIndicesId,
  SectionGroupIndices,
  updateVisitedSectionIndices,
} from '../../../Helpers/assistedApplication/sectionGroups';
import { isInsuredPeopleSectionGroup } from '../../../Helpers/questionnaireAnswers';
import { useAAQuestionnaireVersionContext } from '../../../Hooks';
import {
  useAllSections,
  useNextSectionData,
  usePreviousSectionData,
  useSectionViewPresets,
} from '../../../Hooks/Application/useSectionSelection';
import { useAssistedApplicationContext } from '../../../Hooks/useAssistedApplicationContext';
import { useQuestionnaireEngine } from '../../../Hooks/useQuestionnaireEngine';
import { useFullPageLoaderContext } from '../../LoadingView/FullPageLoader';
import { SkipLink } from '../../SkipLink/SkipLink';
import { NumberOfVisibleErrorsBySectionGroup } from '../AssistedApplicationContainer';
import { DeleteProposedInsuredModal } from '../Modals/ProposedInsured/DeleteProposedInsuredModal';
import { ApplicationDetailsWidgetsContainer } from './ApplicationDetailsWidgets/ApplicationDetailsWidgetsContainer';
import { ApplicationDetailsWidgetsStickyWrapper } from './ApplicationDetailsWidgets/ApplicationDetailsWidgetsStickyWrapper';
import { SectionView } from './AssistedApplicationForm/SectionView';
import { AssistedApplicationNavigation } from './AssistedApplicationNavigation';
import { NavigationButton } from './NavigationButton';
import { ProposedInsuredTabBar } from './ProposedInsuredTabBar/ProposedInsuredTabBar';
import {
  FormContainer,
  NavigationContainer,
  ScrollContainer,
  ScrollContent,
  StyledNextNavigationButton,
  ViewContainer,
} from './Styles';

type Props = {
  renderingQuestionnaire: RenderingQuestionnaire;
  selectedProductId: string;
  isApplicationSubmitted: boolean;
  isApplicationSigned: boolean;
  applicationMode: ApplicationMode;
  isQuestionnaireCompleted: boolean;
  onOpenSubmissionDetailsModal: () => void;
  productsEntity: ProductsEntity<string> | undefined;
  proposedInsuredIndexToDelete: number;
  onOpenDeleteProposedInsuredModal: (index: number) => void;
  onCloseDeleteProposedInsuredModal: () => void;
  onLockApplication?: () => void;
  onUnlockApplication?: () => void;
  onOpenESignatureDetails?: () => void;
  isESignStatusChanging?: boolean;
  applicationSignatureType?: SignatureType;
  isReadOnly?: boolean;
  visitedSectionIndicesByGroup: SectionGroupIndices[] | undefined;
  setVisitedSectionsIndicesByGroup: Dispatch<SetStateAction<SectionGroupIndices[] | undefined>>;
  numberOfVisibleErrorsBySectionGroup: NumberOfVisibleErrorsBySectionGroup;
  showCompletionIcons: boolean;
};

function AssistedApplicationView(props: Props): ReactElement | null {
  const { t } = useTranslation();
  const { questionnaireEngine } = useQuestionnaireEngine();
  const {
    renderingQuestionnaire,
    selectedProductId,
    isApplicationSubmitted,
    isApplicationSigned,
    isReadOnly,
    onOpenSubmissionDetailsModal,
    productsEntity,
    proposedInsuredIndexToDelete,
    onOpenDeleteProposedInsuredModal,
    onCloseDeleteProposedInsuredModal,
    applicationMode,
    visitedSectionIndicesByGroup,
    setVisitedSectionsIndicesByGroup,
  } = props;

  const {
    currentProposedInsuredIndex,
    currentSectionGroupId,
    proposedInsuredTabs,
    onSelectProposedInsured,
    onSwitchSectionGroupTab,
    activeSectionIndex,
    onSetActiveSectionIndex,
  } = useAssistedApplicationContext();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const { closeFullPageLoader, isOpen: fullPageLoaderIsOpen } = useFullPageLoaderContext();
  const [isFirstRender, setIsFirstRender] = useState<boolean>(true);

  const sectionGroup =
    findSectionGroup(renderingQuestionnaire, currentSectionGroupId, currentProposedInsuredIndex) ??
    renderingQuestionnaire[0];

  const sectionContainerRef = useRef<HTMLDivElement>(null);

  const allSections = useAllSections({ sectionGroup, visitedSectionIndicesByGroup });

  useSectionViewPresets(allSections[activeSectionIndex]);

  const nextSectionData = useNextSectionData(renderingQuestionnaire, allSections.length);
  const previousSectionData = usePreviousSectionData(renderingQuestionnaire);

  const navigateToSection = useCallback(
    (nextSectionIndex: number, direction?: Direction): void => {
      // Do nothing if user clicks on the current displayed section.
      if (nextSectionIndex === activeSectionIndex) return;

      const surrogateId = proposedInsuredTabs[currentProposedInsuredIndex]?.surrogateId ?? '';

      let sectionIndicesByGroupId = getSectionGroupWithIndicesId(currentSectionGroupId, surrogateId);

      updateVisitedSectionIndices({
        activeSectionIndex,
        sectionIndicesByGroupId,
        setVisitedSectionsIndicesByGroup,
        visitedSectionIndicesByGroup,
      });

      if (direction) {
        const section = direction === Direction.previous ? previousSectionData : nextSectionData;
        const groupId = section?.groupId ?? currentSectionGroupId;
        const proposedInsuredIndex = section?.proposedInsuredIndex ?? currentProposedInsuredIndex;
        onSwitchSectionGroupTab(groupId);
        onSelectProposedInsured(proposedInsuredIndex);
        sectionIndicesByGroupId = getSectionGroupWithIndicesId(groupId, surrogateId);
      }

      const nextSectionGroup = visitedSectionIndicesByGroup?.find(
        (sectionGroup) => sectionGroup.id === sectionIndicesByGroupId,
      );

      // Show errors if the next section has already been visited before, hide them otherwise
      questionnaireEngine.setDisplayError(nextSectionGroup?.indices.includes(nextSectionIndex) || false);

      // Activate the next section
      onSetActiveSectionIndex(nextSectionIndex);

      sectionContainerRef?.current?.scrollIntoView(true);
    },
    [
      activeSectionIndex,
      currentProposedInsuredIndex,
      currentSectionGroupId,
      nextSectionData,
      onSelectProposedInsured,
      onSwitchSectionGroupTab,
      previousSectionData,
      proposedInsuredTabs,
      visitedSectionIndicesByGroup,
      onSetActiveSectionIndex,
      questionnaireEngine,
      setVisitedSectionsIndicesByGroup,
    ],
  );

  // Check for the completion of the question to render prompt to lock the application
  const { maxNumberOfInsured } = useAAQuestionnaireVersionContext();

  // ------------------------------------

  const section = useMemo(() => {
    const activeSection = getSectionById(sectionGroup, allSections[activeSectionIndex]?.id);
    if (!activeSection) {
      onSetActiveSectionIndex(0);
      return allSections[0];
    }
    return activeSection;
  }, [sectionGroup, allSections, activeSectionIndex, onSetActiveSectionIndex]);

  useEffect(() => {
    if (section && fullPageLoaderIsOpen && isFirstRender) {
      closeFullPageLoader();
      setIsFirstRender(false);
    }
  }, [section, fullPageLoaderIsOpen, closeFullPageLoader, isFirstRender]);

  if (!section) return null;

  const withInsuredTabs = maxNumberOfInsured > 1 && isInsuredPeopleSectionGroup(sectionGroup);
  const skipToAssistedApplicationFormId = 'assistedApplicationForm';

  return (
    <Fragment>
      {withInsuredTabs && (
        <Fragment>
          <ProposedInsuredTabBar
            maxTabs={maxNumberOfInsured}
            onOpenDeleteProposedInsuredModal={onOpenDeleteProposedInsuredModal}
            showCompletionIcons={props.showCompletionIcons}
          />
          <DeleteProposedInsuredModal
            isOpen={proposedInsuredIndexToDelete !== -1}
            onClose={onCloseDeleteProposedInsuredModal}
            proposedInsuredIndexToDelete={proposedInsuredIndexToDelete}
          />
        </Fragment>
      )}

      <ViewContainer $withInsuredTabs={withInsuredTabs}>
        <NavigationContainer>
          <SkipLink href={`#${skipToAssistedApplicationFormId}`} title={t('skipLinks.navigation')} />
          <AssistedApplicationNavigation
            scrollContainerRef={scrollContainerRef}
            activeSectionIndex={activeSectionIndex}
            onSectionClick={navigateToSection}
            hasSectionEndIcons={applicationMode !== ApplicationModes.paper && !isReadOnly}
            allSections={allSections}
          />
        </NavigationContainer>
        <ScrollContainer ref={scrollContainerRef}>
          <ScrollContent>
            <FormContainer
              id={skipToAssistedApplicationFormId}
              flex={2}
              mx={8}
              {...{ ref: sectionContainerRef }}
              role='form'
            >
              <SectionView section={section} />
              <Divider />
              <Box display='flex' py={3}>
                {previousSectionData?.section && (
                  <NavigationButton
                    direction={Direction.previous}
                    onClick={() => navigateToSection(previousSectionData.index, Direction.previous)}
                  >
                    {previousSectionData.section.title}
                  </NavigationButton>
                )}
                {nextSectionData?.section && (
                  <StyledNextNavigationButton
                    direction={Direction.next}
                    onClick={() => navigateToSection(nextSectionData.index, Direction.next)}
                  >
                    {nextSectionData.section.title}
                  </StyledNextNavigationButton>
                )}
              </Box>
            </FormContainer>
            <ApplicationDetailsWidgetsStickyWrapper flex={1} mt={5} role='region'>
              <ApplicationDetailsWidgetsContainer
                selectedProductId={selectedProductId}
                isApplicationSubmitted={isApplicationSubmitted}
                isApplicationSigned={isApplicationSigned}
                onOpenSubmissionDetailsModal={onOpenSubmissionDetailsModal}
                productsEntity={productsEntity}
              />
            </ApplicationDetailsWidgetsStickyWrapper>
          </ScrollContent>
        </ScrollContainer>
      </ViewContainer>
    </Fragment>
  );
}

export const MemoizedAssistedApplicationView = memo(AssistedApplicationView);
